import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import SearchInput from "../../../../../../../components/SearchInput";
import CustomTabs from "../../../../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../../../../components/app/StyledButton";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import useDisclosure from "../../../../../../../hooks/use-disclosure";
import useFcmSmsEventListener from "../../../../../../../services/fcm/listeners/FcmSmsEventListener";
import { roleFilter } from "../../../../../../../utils/utils";
import SendBulkEmailModal from "../../../../communications/email/SendBulkEmailModal";
import PatientCallTable from "./PatientCallTable";
import PatientChat from "./PatientChat";
import PatientEmailTable, {
  EmailCommunicationFilters,
} from "./PatientEmailTable";

/** Map tab value → component for minimized (visit-note) view */
const TAB_COMPONENTS: Record<string, ReactNode> = {
  "": <PatientChat />,
  email: <PatientEmailTable />,
  call: <PatientCallTable />,
};

const PatientCommunicationLayoutNew = () => {
  const isVisitNotePage = window.location.pathname.includes("/visit-note");
  const [patientData] = useSelectedPatient();

  const [smsCount] = useFcmSmsEventListener({});

  const sendMailDisclosure = useDisclosure();
  useEffect(() => {}, [smsCount]);

  const [activeButton, setActiveButton] = useState<string>(() => {
    if (isVisitNotePage) return "";
    const tab = (location.pathname.split("/").pop() as string) || "";
    return tab === "communication" ? "" : tab;
  });

  const navigate = useNavigate();
  const { hasView: hasViewEmail } = useRoleController("Communication > eMail");
  const { hasView: hasViewChat } = useRoleController("Communication > Chat");
  const { hasView: hasViewSms } = useRoleController("Communication > SMS");
  const [emailFilter, setEmailFilter] = useState<EmailCommunicationFilters>({
    searchString: undefined,
    archive: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const COMMUNICATION_TABS = [
    {
      value: "",
      label: "SMS/Text",
      count: smsCount,
    },
    {
      value: "email",
      label: "Email",
    },
    {
      value: "call",
      label: "Call",
    },
  ];

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ChartActionStrip
        startContent={
          <CustomTabs
            tabsData={roleFilter({
              list: COMMUNICATION_TABS,
              compWith: {
                email: hasViewEmail,
                chat: hasViewChat,
                sms: hasViewSms,
              },
            })}
            value={activeButton}
            onChange={(v) => {
              setActiveButton(v);
              if (!isVisitNotePage) navigate(v);
            }}
          />
        }
        endContent={
          activeButton === "email" && !isVisitNotePage ? (
            <>
              <SearchInput
                searchKey={emailFilter.searchString ?? ""}
                placeholder="Search by Subject"
                onSearch={(v: string) => {
                  setEmailFilter((prev) => ({
                    ...prev,
                    searchString: v,
                  }));
                }}
              />
              <StyledButtonV2
                startIcon={<AddIcon />}
                onClick={() => {
                  sendMailDisclosure.open([
                    {
                      uuid: patientData?.uuid,
                      name: `${patientData?.firstName} ${patientData?.lastName}`,
                    },
                  ]);
                }}
              >
                Compose Email
              </StyledButtonV2>
              <SendBulkEmailModal
                disClosureHost={sendMailDisclosure}
                disableAddPatient
              />
            </>
          ) : undefined
        }
      />

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isVisitNotePage ? (
          (TAB_COMPONENTS[activeButton] ?? TAB_COMPONENTS[""])
        ) : (
          <Outlet context={{ patientData, emailFilter }} />
        )}
      </Box>
    </Box>
  );
};

export default PatientCommunicationLayoutNew;
